import { Link, useNavigate } from "react-router-dom";
import { Compass, Menu, X, Briefcase, LogOut, Phone, Mail, MapPin, Facebook, Instagram, Linkedin, Twitter, MessageCircle, Star, Map as MapIcon, ChevronDown, Check } from "lucide-react";
import { useState } from "react";
import { isAuthed, getUser, clearToken } from "../../lib/auth";
import { useMarket, MARKETS } from "../../lib/market";
import MapPlanner from "./MapPlanner";

function MarketSwitcher() {
  const { market, setMarket } = useMarket();
  const [open, setOpen] = useState(false);
  const active = MARKETS[market];
  return (
    <div className="relative" data-testid="market-switcher">
      <button
        onClick={() => setOpen((o) => !o)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        data-testid="market-switcher-btn"
        className="flex items-center gap-1.5 border border-sand-dark hover:border-moss px-2.5 py-1.5 rounded-sm text-sm font-semibold transition-colors"
        title="Choose your market">
        <span className="text-base leading-none">{active.flag}</span>
        <span className="hidden lg:inline">{active.label}</span>
        <ChevronDown className="w-3.5 h-3.5 text-ink-muted" />
      </button>
      {open && (
        <div className="absolute right-0 mt-1.5 w-44 bg-white border border-sand-dark rounded-sm shadow-lg overflow-hidden z-[800]" data-testid="market-menu">
          {Object.values(MARKETS).map((m) => (
            <button key={m.id} data-testid={`market-option-${m.id}`}
              onMouseDown={() => { setMarket(m.id); setOpen(false); }}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-left hover:bg-sand transition-colors ${m.id === market ? "font-bold text-terracotta" : "text-ink"}`}>
              <span className="text-base leading-none">{m.flag}</span>
              <span className="flex-1">{m.label}</span>
              <span className="text-[10px] font-mono text-ink-muted">{m.currencyCode}</span>
              {m.id === market && <Check className="w-3.5 h-3.5 text-terracotta" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const SOCIALS = [
  { label: "Facebook", href: "https://www.facebook.com/responsibleadventures/", icon: Facebook },
  { label: "Instagram", href: "https://www.instagram.com/responsible_adventures/", icon: Instagram },
  { label: "LinkedIn", href: "https://www.linkedin.com/company/responsibleadventures/", icon: Linkedin },
  { label: "X (Twitter)", href: "https://twitter.com/responsible_adv", icon: Twitter },
  { label: "WhatsApp", href: "https://wa.me/9779801082108", icon: MessageCircle },
];
const TRIPADVISOR_URL = "https://www.tripadvisor.com.au/Attraction_Review-g293890-d10202865-Reviews-Responsible_Adventures-Kathmandu_Kathmandu_Valley_Bagmati_Zone_Central_Region.html";

export const CATEGORIES = [
  { id: "trekking", label: "Trekking" },
  { id: "wellness", label: "Wellness" },
  { id: "motorcycle", label: "Motorcycle" },
  { id: "cycling", label: "Cycling" },
  { id: "flying", label: "Flying" },
  { id: "culture", label: "Cultural" },
];

export function RAHeader() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const authed = isAuthed();
  const user = getUser();
  const logout = () => { clearToken(); navigate("/"); window.location.reload(); };

  return (
    <header className="sticky top-0 z-[700] bg-base/90 backdrop-blur-md border-b border-sand-dark" data-testid="ra-header">
      <div className="max-w-7xl mx-auto flex items-center justify-between px-5 sm:px-8 h-[68px]">
        <Link to="/" className="flex items-center gap-2.5" data-testid="ra-logo">
          <div className="w-9 h-9 bg-terracotta rounded-sm flex items-center justify-center">
            <Compass className="w-5 h-5 text-white" strokeWidth={2.2} />
          </div>
          <div className="leading-none">
            <div className="font-display font-extrabold text-[15px] tracking-tight">RESPONSIBLE ADVENTURES</div>
            <div className="font-mono text-[9px] tracking-[0.25em] uppercase text-moss mt-0.5">Beyond the Ordinary</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-6 font-medium text-sm text-ink-secondary">
          <Link to="/" className="hover:text-terracotta transition-colors" data-testid="nav-home">Home</Link>
          <Link to="/tours" className="hover:text-terracotta transition-colors" data-testid="nav-tours">All Tours</Link>
          <Link to="/about" className="hover:text-terracotta transition-colors" data-testid="nav-about">About</Link>
          <Link to="/blog" className="hover:text-terracotta transition-colors" data-testid="nav-blog">Blog</Link>
          <Link to="/contact" className="hover:text-terracotta transition-colors" data-testid="nav-contact">Contact</Link>
          <MapPlanner triggerClassName="flex items-center gap-1.5 text-terracotta hover:text-terracotta-hover font-semibold transition-colors" triggerLabel="Map Planner" />
        </nav>

        <div className="flex items-center gap-3">
          <MarketSwitcher />
          {authed ? (
            <>
              <Link to="/build" data-testid="ra-portal-btn" className="hidden sm:flex items-center gap-1.5 bg-moss hover:bg-moss-hover text-white px-3.5 py-2 rounded-sm text-sm font-semibold transition-colors">
                <Briefcase className="w-4 h-4" /> Trade portal
              </Link>
              <button onClick={logout} data-testid="ra-logout" className="hidden sm:flex items-center gap-1.5 text-ink-muted hover:text-terracotta text-xs font-mono uppercase tracking-widest transition-colors">
                <LogOut className="w-3.5 h-3.5" /> Sign out
              </button>
            </>
          ) : (
            <Link to="/trade" data-testid="ra-tradelogin-btn" className="hidden sm:flex items-center gap-1.5 border border-sand-dark hover:border-moss px-3.5 py-2 rounded-sm text-sm font-semibold transition-colors">
              <Briefcase className="w-4 h-4 text-moss" /> Trade login
            </Link>
          )}
          <button className="md:hidden p-2" onClick={() => setOpen((o) => !o)} data-testid="ra-menu-toggle">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-sand-dark bg-base px-5 py-4 space-y-3" data-testid="ra-mobile-nav">
          <Link to="/" onClick={() => setOpen(false)} className="block text-sm font-medium">Home</Link>
          <Link to="/tours" onClick={() => setOpen(false)} className="block text-sm font-medium">All Tours</Link>
          <Link to="/about" onClick={() => setOpen(false)} className="block text-sm font-medium">About</Link>
          <Link to="/blog" onClick={() => setOpen(false)} className="block text-sm font-medium">Blog</Link>
          <Link to="/contact" onClick={() => setOpen(false)} className="block text-sm font-medium">Contact</Link>
          <div className="pt-2 border-t border-sand-dark">
            <p className="font-mono text-[10px] uppercase tracking-widest text-ink-muted mb-2">Tours by type</p>
            {CATEGORIES.map((c) => (
              <Link key={c.id} to={`/tours?category=${c.id}`} onClick={() => setOpen(false)} className="block text-sm text-ink-secondary py-0.5">{c.label}</Link>
            ))}
          </div>
          <Link to={authed ? "/build" : "/trade"} onClick={() => setOpen(false)} className="block text-sm font-semibold text-moss">{authed ? "Trade portal" : "Trade login"}</Link>
          {authed && <button onClick={() => { setOpen(false); logout(); }} className="block text-sm text-ink-muted">Sign out</button>}
        </div>
      )}
    </header>
  );
}

export function RAFooter() {
  return (
    <footer className="bg-ink text-white/80 mt-20" data-testid="ra-footer">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14 grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <div className="flex items-center gap-2.5 mb-4">
            <div className="w-9 h-9 bg-terracotta rounded-sm flex items-center justify-center"><Compass className="w-5 h-5 text-white" /></div>
            <span className="font-display font-extrabold text-white tracking-tight">RESPONSIBLE ADVENTURES</span>
          </div>
          <p className="text-sm leading-relaxed text-white/60">Carbon-neutral Himalayan journeys across Nepal, Bhutan, India & Tibet. Tourism Licence 48588/064/065.</p>
          <div className="flex items-center gap-2.5 mt-5" data-testid="footer-socials">
            {SOCIALS.map((s) => {
              const Icon = s.icon;
              return (
                <a key={s.label} href={s.href} target="_blank" rel="noreferrer" aria-label={s.label}
                  data-testid={`social-${s.label.toLowerCase().split(" ")[0]}`}
                  className="w-9 h-9 rounded-sm bg-white/10 hover:bg-terracotta flex items-center justify-center transition-colors">
                  <Icon className="w-4 h-4 text-white" />
                </a>
              );
            })}
          </div>
          <a href={TRIPADVISOR_URL} target="_blank" rel="noreferrer" data-testid="footer-tripadvisor"
            className="inline-flex items-center gap-1.5 mt-4 text-xs text-white/60 hover:text-terracotta transition-colors">
            <Star className="w-3.5 h-3.5 fill-terracotta text-terracotta" /> 5.0 on Tripadvisor
          </a>
        </div>
        <div>
          <h4 className="font-mono text-xs uppercase tracking-widest text-white/50 mb-4">Tours</h4>
          <ul className="space-y-2 text-sm">
            {CATEGORIES.map((c) => <li key={c.id}><Link to={`/tours?category=${c.id}`} className="hover:text-terracotta transition-colors">{c.label}</Link></li>)}
          </ul>
        </div>
        <div>
          <h4 className="font-mono text-xs uppercase tracking-widest text-white/50 mb-4">Destinations</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/destinations/nepal" className="hover:text-terracotta transition-colors">Nepal</Link></li>
            <li><Link to="/destinations/bhutan" className="hover:text-terracotta transition-colors">Bhutan</Link></li>
            <li><Link to="/destinations/tibet" className="hover:text-terracotta transition-colors">Tibet</Link></li>
            <li><Link to="/destinations/india" className="hover:text-terracotta transition-colors">India — Himalaya</Link></li>
            <li><Link to="/destinations/kyrgyzstan" className="hover:text-terracotta transition-colors">Kyrgyzstan</Link></li>
            <li><Link to="/destinations/pakistan" className="hover:text-terracotta transition-colors">Pakistan</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="font-mono text-xs uppercase tracking-widest text-white/50 mb-4">Contact</h4>
          <ul className="space-y-2.5 text-sm">
            <li className="flex items-center gap-2"><Phone className="w-4 h-4 text-terracotta" /> +977 9801082108</li>
            <li className="flex items-center gap-2"><Mail className="w-4 h-4 text-terracotta" /> raj@responsibleadventures.com</li>
            <li className="flex items-start gap-2"><MapPin className="w-4 h-4 text-terracotta mt-0.5" /> 17 Sundarnagar, Amrit Marg, Kathmandu 44600</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10 py-5 text-center text-xs text-white/40">© {new Date().getFullYear()} Responsible Adventures — Powered by Wayfinder trip builder</div>
    </footer>
  );
}

export default function RALayout({ children }) {
  return (
    <div className="min-h-screen bg-base text-ink flex flex-col">
      <RAHeader />
      <main className="flex-1">{children}</main>
      <RAFooter />
    </div>
  );
}
